import type { LucideIcon } from 'lucide-react';
import {
  Stethoscope,
  GraduationCap,
  HeartPulse,
  Scan,
  TestTube,
  Activity,
  Dna,
  Building2,
  Pill,
  Globe2,
  Smartphone,
  Gamepad2,
} from 'lucide-react';

export interface Division {
  id: number;
  name: string;
  tagline: string;
  description: string;
  icon: LucideIcon;
  url: string;
  gradient: string;
  status: string;
  stats: Record<string, string>;
  color: string;
  productPageUrl?: string;
}

export const divisions: Division[] = [
  {
    id: 1,
    name: 'OmniAlera Medical Platform',
    tagline: 'Global Medical Education Excellence',
    description: 'Comprehensive medical education ecosystem with 10 sub-platforms including exam preparation (USMLE, PLAB, NCLEX), Live App Generator, OmniMed Center practice management, Aegis Legal compliance tools, and pharmacy professional resources.',
    icon: Stethoscope,
    url: 'https://omnialera-medical-platform.netlify.app',
    gradient: 'from-blue-500 via-purple-500 to-pink-500',
    status: 'Live',
    stats: { code: '196K+', value: 'Production', market: 'Global' },
    color: 'blue'
  },
  {
    id: 2,
    name: 'MedCloudMentor',
    tagline: 'Nigerian Education Revolution',
    description: '75,000+ curated exam questions for JAMB, WAEC, NECO, and Post-UTME. Serving 1.8M annual Nigerian students preparing for university entrance.',
    icon: GraduationCap,
    url: 'https://medcloudmentor.netlify.app/',
    gradient: 'from-indigo-500 via-purple-500 to-pink-500',
    status: 'Live',
    stats: { questions: 'Target: 75K+', students: 'Target: 1.8M', value: 'Production' },
    color: 'indigo'
  },
  {
    id: 3,
    name: 'OmniClinicalBridge',
    tagline: 'African Healthcare Empowerment',
    description: 'Offline-capable clinical decision support for resource-limited settings. Evidence-based protocols with WHO Essential Medicines and multi-language support.',
    icon: HeartPulse,
    url: 'https://omniclinicalbridge-v2.netlify.app',
    gradient: 'from-rose-500 via-pink-500 to-purple-500',
    status: 'Live',
    stats: { conditions: '100+', medicines: '50+ WHO', languages: '5' },
    color: 'rose'
  },
  {
    id: 4,
    name: 'RadiantScanAI',
    tagline: 'AI-Powered Radiology Intelligence',
    description: 'Advanced radiology interpretation with 46 study protocols. Addressing Africa\'s critical radiologist shortage with AI-assisted diagnostics.',
    icon: Scan,
    url: 'https://radiantscan-ai.netlify.app',
    gradient: 'from-blue-400 via-purple-400 to-pink-400',
    status: 'Live',
    stats: { protocols: '50+', modalities: '10+', ai: 'GPT-4' },
    color: 'blue'
  },
  {
    id: 5,
    name: 'HemaAI',
    tagline: 'Intelligent Laboratory Analysis',
    description: 'Instant interpretation of laboratory results. CBC, comprehensive metabolic panels, and coagulation studies with AI-powered clinical insights.',
    icon: TestTube,
    url: 'https://hema-ai.netlify.app',
    gradient: 'from-red-500 via-rose-500 to-pink-500',
    status: 'Live',
    stats: { tests: '50+', accuracy: 'Target 98%', speed: 'Target <1s' },
    color: 'red'
  },
  {
    id: 6,
    name: 'ApexVitalsAI',
    tagline: 'Predictive Patient Monitoring',
    description: 'Remote patient monitoring with predictive deterioration alerts. Real-time vital signs analysis with AI-powered early warning system.',
    icon: Activity,
    url: 'https://apexvitals-ai.netlify.app',
    gradient: 'from-green-500 via-teal-500 to-cyan-500',
    status: 'Live',
    stats: { vitals: '24/7', alerts: 'Real-time', ml: 'Advanced' },
    color: 'green'
  },
  {
    id: 7,
    name: 'GenomicCareOS',
    tagline: 'Personalized Medicine Revolution',
    description: 'AI pharmacogenomics platform analyzing drug-gene interactions. Optimize dosing based on genetic profiles for safer, more effective treatment.',
    icon: Dna,
    url: 'https://genomiccare-os.netlify.app',
    gradient: 'from-indigo-500 via-purple-500 to-pink-500',
    status: 'Live',
    stats: { genes: 'Target 500+', drugs: 'Target 200+', precision: 'Target Individual' },
    color: 'indigo'
  },
  {
    id: 8,
    name: 'HospitalOpsAI',
    tagline: 'Hospital Intelligence Platform',
    description: 'AI-powered hospital operations optimization. Bed management, staff scheduling, and patient flow analytics for maximum efficiency.',
    icon: Building2,
    url: 'https://hospitalops-ai.netlify.app',
    gradient: 'from-slate-500 via-gray-500 to-zinc-500',
    status: 'Coming Q4 2026',
    stats: { efficiency: 'Target +40%', costs: 'Target -25%', satisfaction: 'Target +35%' },
    color: 'slate'
  },
  {
    id: 9,
    name: 'PharmaRealTime',
    tagline: 'Pan-African Drug Intelligence',
    description: 'Real-time drug shortage prediction and pharmacy inventory management. AI-powered supply chain optimization across African markets.',
    icon: Pill,
    url: 'https://pharma-realtime.netlify.app',
    gradient: 'from-orange-500 via-amber-500 to-yellow-500',
    status: 'Coming Q4 2026',
    stats: { pharmacies: 'Target 10K+', drugs: 'Target 5K+', coverage: 'Target 54' },
    color: 'orange'
  },
  {
    id: 10,
    name: 'DiseaseAtlasAI',
    tagline: 'Global Disease Surveillance',
    description: 'Real-time disease tracking and outbreak prediction. AI-powered epidemiological intelligence for proactive public health response.',
    icon: Globe2,
    url: 'https://disease-atlas-ai.netlify.app',
    gradient: 'from-emerald-500 via-teal-500 to-cyan-500',
    status: 'Coming Q4 2026',
    stats: { diseases: 'Target 100+', countries: 'Target Global', predictions: 'AI-powered' },
    color: 'emerald'
  },
  {
    id: 11,
    name: 'Consumer Apps',
    tagline: 'Health & Wellness for Everyone',
    description: 'Six consumer applications putting accessible health technology in users\' hands. From pregnancy tracking to medical mentorship and AI-powered health tools, we\'re building consumer-facing products for African and global users.',
    icon: Smartphone,
    url: '/divisions/consumer-apps',
    gradient: 'from-fuchsia-500 via-pink-500 to-purple-500',
    status: 'Live',
    stats: { apps: '6 Apps', live: '2 in Production', categories: 'Health & Wellness' },
    color: 'fuchsia'
  },
  {
    id: 12,
    name: 'Gaming',
    tagline: 'Literary Life Simulators & Beyond',
    description: 'A 40-game portfolio funding African healthcare through immersive consumer experiences. Starting with LifeCraft — an 8-chapter literary life simulator across 67 cultures — we\'re building games that earn their runtime through prose quality and meaningful decisions.',
    icon: Gamepad2,
    url: '/divisions/gaming',
    gradient: 'from-blue-500 via-indigo-500 to-purple-500',
    status: 'Live',
    stats: { games: '40 Planned', flagship: 'LifeCraft', cultures: '67' },
    color: 'indigo',
    productPageUrl: '/lifecraft'
  },
];
